import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getFeatures, FEATURES_FAIL_CLOSED, type FeatureFlags } from '../lib/api';
import { logger } from '../lib/logger';

interface FeaturesContextType extends FeatureFlags {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const FeaturesContext = createContext<FeaturesContextType | null>(null);

export function useFeatures(): FeaturesContextType {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
}

export function FeaturesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [features, setFeatures] = useState<FeatureFlags>(FEATURES_FAIL_CLOSED);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFeatures = useCallback(async (forceRefresh: boolean = false) => {
    if (!isAuthenticated) {
      setFeatures(FEATURES_FAIL_CLOSED);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getFeatures(forceRefresh);
      setFeatures(data);
      setError(null);
      logger.debug('Features loaded', data, 'FEATURES');
    } catch (err: any) {
      logger.error('Failed to fetch features, failing closed', err, 'FEATURES');
      setFeatures(FEATURES_FAIL_CLOSED);
      setError(err?.message || 'Failed to load features');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading) {
      fetchFeatures();
    }
  }, [authLoading, fetchFeatures]);

  const contextValue: FeaturesContextType = {
    ...features,
    loading,
    error,
    refetch: () => fetchFeatures(true),
  };

  return (
    <FeaturesContext.Provider value={contextValue}>
      {children}
    </FeaturesContext.Provider>
  );
}
